import {
  Brand,
  CreateBrandRequest,
  UpdateBrandRequest,
  Post,
  CreatePostRequest,
  UpdatePostRequest,
  GeneratePostsRequest,
  GeneratePostsResponse,
  Workspace,
  CreateWorkspaceRequest,
  DashboardStats,
  User,
  PlatformConnection,
  OnboardingProfile,
  ApiKey,
  DataEventItem,
  ContentCadence,
  UpsertCadenceRequest,
  ReviewPost,
  AnalyticsOverview,
  PostAnalyticsItem,
  PostTimeseriesPoint,
  PlatformTimeseriesPoint,
  LinkedInOrgSummary,
  LinkedInOrgHistory,
  SocialPlatform,
  AdCampaign,
  AdSet,
  AdCreative,
  CampaignInsightsResponse,
  AdminUser,
  AdminUsageOverview,
  ManualPostRequest,
  WorkspaceAiUsage,
  AdminAiUsageSummary,
  AdminAiSettingsResponse,
  UpdateAdminAiSettingsRequest,
  AdminWorkspaceAiUsage,
  SchedulerConfig,
  ApiCallLogRow,
  ApiCallLogStats,
  ImportJob,
  BlogAnalyticsSummary,
} from '@/types';

import { api } from './client';

export const analyticsApi = {
  overview: (workspaceId: string, from?: string, to?: string, brandId?: string) =>
    api
      .get<AnalyticsOverview>(`/api/workspaces/${workspaceId}/analytics`, {
        params: { from, to, brandId },
      })
      .then((r) => r.data),
  posts: (
    workspaceId: string,
    params?: { sort?: string; dir?: string; page?: number; size?: number },
  ) =>
    api
      .get<PostAnalyticsItem[]>(`/api/workspaces/${workspaceId}/analytics/posts`, { params })
      .then((r) => r.data),
  sync: (workspaceId: string) =>
    api
      .post<{ synced: number }>(`/api/workspaces/${workspaceId}/analytics/sync`)
      .then((r) => r.data),
  postTimeseries: (workspaceId: string, postId: string, days: number = 30) =>
    api
      .get<
        PostTimeseriesPoint[]
      >(`/api/workspaces/${workspaceId}/analytics/posts/${postId}/timeseries`, { params: { days } })
      .then((r) => r.data),
  postsOverTime: (workspaceId: string, days: number = 30) =>
    api
      .get<
        PostsOverTimePoint[]
      >(`/api/workspaces/${workspaceId}/analytics/posts-over-time`, { params: { days } })
      .then((r) => r.data),
  /** V94 — daily aggregate of every metric for the given platform across the
   *  workspace. Drives the per-platform tab hero charts. */
  platformTimeseries: (
    workspaceId: string,
    platform: SocialPlatform,
    from?: string,
    to?: string,
    connectionId?: string,
  ) =>
    api
      .get<PlatformTimeseriesPoint[]>(
        `/api/workspaces/${workspaceId}/analytics/platform-timeseries`,
        {
          params: { platform, from, to, connectionId },
        },
      )
      .then((r) => r.data),

  /** V168 — LinkedIn company pages + latest org-stats snapshot. Optional brand filter. */
  linkedinOrgs: (workspaceId: string, brandId?: string) =>
    api
      .get<LinkedInOrgSummary[]>(`/api/workspaces/${workspaceId}/analytics/linkedin/orgs`, {
        params: { brandId },
      })
      .then((r) => r.data),

  /** V168 — daily org-stats history for one LinkedIn page + latest breakdown JSON. */
  linkedinOrgHistory: (workspaceId: string, connectionId: string, windowDays = 90) =>
    api
      .get<LinkedInOrgHistory>(
        `/api/workspaces/${workspaceId}/analytics/linkedin/orgs/${connectionId}/history`,
        { params: { windowDays } },
      )
      .then((r) => r.data),

  /**
   * Audit-2026-05-08 #1 — analytics CSV export. Sync streaming
   * response; available on every paid plan + trial. The download is
   * triggered by setting `window.location.href` to the URL with the
   * Authorization header attached via a Blob download (since browsers
   * don't ship axios bearer headers on a plain `<a href>` click).
   */
  exportCsv: (
    workspaceId: string,
    params: { brandId?: string; platform?: string; from?: string; to?: string },
  ) =>
    api
      .get<Blob>(`/api/workspaces/${workspaceId}/analytics/export/csv`, {
        params,
        responseType: 'blob',
      })
      .then((r) => ({
        blob: r.data,
        filename:
          (r.headers['content-disposition'] as string | undefined)?.match(
            /filename="?([^"]+)"?/,
          )?.[1] ?? `merkflow-analytics-${params.from ?? ''}-to-${params.to ?? ''}.csv`,
      })),

  /**
   * Analytics PDF export — async ReportStrategy chain, same as the AI
   * Visibility PDF. Returns the report job id to poll. Plan-gated
   * server-side via `analytics_export_pdf` (V120 — trial+pro+agency);
   * Starter gets 403 + `feature_not_available` body, which the global
   * 402/403 axios path turns into an upgrade modal.
   */
  exportPdf: (workspaceId: string, params: { brandId?: string; from?: string; to?: string }) =>
    api
      .post<{
        reportId: string;
        status: string;
      }>(`/api/workspaces/${workspaceId}/analytics/export/pdf`, null, { params })
      .then((r) => r.data),
};

/** One row per day in the Overview "Posts published" bar chart. */
export interface PostsOverTimePoint {
  date: string; // YYYY-MM-DD
  instagram: number;
  facebook: number;
  linkedin: number;
  twitter: number;
  tiktok: number;
  total: number;
}

export const adsApi = {
  listCampaigns: (workspaceId: string) =>
    api.get<AdCampaign[]>(`/api/workspaces/${workspaceId}/ads/campaigns`).then((r) => r.data),
  /**
   * V143 — `refresh=true` hits Meta `/{metaCampaignId}?fields=effective_status`
   * and drift-syncs the local `status` column. The detail page passes
   * it on mount so the UI reflects whatever Meta actually thinks the
   * campaign is doing right now (PENDING_REVIEW, PAUSED_BY_BILLING, etc.).
   * Off by default so the list page doesn't pay the Graph round trip
   * per row.
   */
  getCampaign: (workspaceId: string, campaignId: string, opts?: { refresh?: boolean }) =>
    api
      .get<AdCampaign>(`/api/workspaces/${workspaceId}/ads/campaigns/${campaignId}`, {
        params: opts?.refresh ? { refresh: true } : undefined,
      })
      .then((r) => r.data),
  /**
   * V143 — boost an existing published Facebook/Instagram post. The
   * post IS the creative (referenced via `object_story_id` on Meta);
   * the request body only carries budget + audience + duration.
   * Lands as DRAFT/PAUSED; user reviews on the detail page and
   * clicks Publish to start spending (which runs the pre-flight
   * gates from V143).
   */
  promotePost: (
    workspaceId: string,
    postId: string,
    data: {
      dailyBudget: number;
      countries: string[];
      ageMin?: number;
      ageMax?: number;
      durationDays?: number;
      name?: string;
    },
  ) =>
    api
      .post<AdCampaign>(`/api/workspaces/${workspaceId}/ads/promote-post/${postId}`, data)
      .then((r) => r.data),
  createCampaign: (
    workspaceId: string,
    data: {
      brandId?: string;
      name: string;
      objective: string;
      dailyBudget?: number;
      startDate?: string;
      endDate?: string;
    },
  ) =>
    api.post<AdCampaign>(`/api/workspaces/${workspaceId}/ads/campaigns`, data).then((r) => r.data),
  createAdSet: (
    workspaceId: string,
    campaignId: string,
    data: {
      name: string;
      targeting: Record<string, any>;
      bidAmount?: number;
      billingEvent?: string;
    },
  ) =>
    api
      .post<AdSet>(`/api/workspaces/${workspaceId}/ads/campaigns/${campaignId}/adsets`, data)
      .then((r) => r.data),
  createCreative: (
    workspaceId: string,
    campaignId: string,
    adSetId: string,
    data: {
      postId?: string;
      headline?: string;
      description?: string;
      imageUrl?: string;
      ctaType?: string;
    },
  ) =>
    api
      .post<AdCreative>(
        `/api/workspaces/${workspaceId}/ads/campaigns/${campaignId}/adsets/${adSetId}/creatives`,
        data,
      )
      .then((r) => r.data),
  publishCampaign: (workspaceId: string, campaignId: string) =>
    api
      .post(`/api/workspaces/${workspaceId}/ads/campaigns/${campaignId}/publish`)
      .then((r) => r.data),
  pauseCampaign: (workspaceId: string, campaignId: string) =>
    api
      .post(`/api/workspaces/${workspaceId}/ads/campaigns/${campaignId}/pause`)
      .then((r) => r.data),
  deleteCampaign: (workspaceId: string, campaignId: string) =>
    api.delete(`/api/workspaces/${workspaceId}/ads/campaigns/${campaignId}`).then((r) => r.data),
  /**
   * V142 — extended response with `{totals, daily, since, until, fromCache}`.
   * Optional window pickers: `days` (1..90, default 30) OR explicit
   * `since` + `until` (ISO yyyy-mm-dd). Backend is DB-first; falls back to
   * a live Meta call when no nightly sync has run yet (fromCache=false).
   */
  getInsights: (
    workspaceId: string,
    campaignId: string,
    opts?: { days?: number; since?: string; until?: string },
  ) =>
    api
      .get<CampaignInsightsResponse>(
        `/api/workspaces/${workspaceId}/ads/campaigns/${campaignId}/insights`,
        { params: opts },
      )
      .then((r) => r.data),
};

// V115 — old waitlistApi removed. The new public POST + admin endpoints
// live higher in this file (waitlistApi + adminWaitlistApi), pointing at
// /api/public/waitlist + /api/admin/waitlist respectively.

// V48 — billing is now workspace-scoped. Old user-level routes were removed.

export interface BillingStatus {
  plan: string; // trial | starter | pro | agency | paused
  planOverride: boolean;
  trialEndsAt: string | null;
  trialDaysRemaining: number | null;
  renewalDate: string | null;
  mollieSubscriptionId: string | null;
  // Non-null = workspace once had a paid Mollie checkout. Lets the
  // BillingPage distinguish "paused trial" (no Mollie history → trial-
  // ended copy) from "paused subscription" (cancelled or payment-failed
  // → subscription-ended copy).
  mollieCustomerId: string | null;
  postsUsed: number;
  postsLimit: number;
  imagesUsed: number;
  imagesLimit: number;
  reelsUsed: number;
  reelsLimit: number;
  brandsUsed: number;
  brandsLimit: number;
  /**
   * Per-feature gate map keyed by FeatureFlag.key, e.g.
   * `"ai_search_visibility": true`. Drives `useFeatureAccess` so
   * Starter clients can render locked CTAs proactively instead of
   * clicking through to a backend 403.
   */
  features: Record<string, boolean>;
}

export interface BillingCheckoutRequest {
  plan: 'starter' | 'pro' | 'agency';
  interval?: 'monthly' | 'annual';
}
